// Turning a Google Places pick into coordinates + address fields for the
// venue forms (AddBusiness "Business Location" step, EditBusinessPage).
//
// Why Places details first: since 4 Jul 2026 (7ffe38b) the forms load Maps
// with REACT_APP_GOOGLE_MAPS_API_KEY, which may not be allowed to use the
// Geocoding API (REQUEST_DENIED). getGeocode() then rejected, nothing caught
// it, and Next always said "Please enter your business address". Place
// details for the chosen suggestion (Places API) returns geometry and address
// components in one call, so geocoding is only a fallback.

import { getDetails, getGeocode } from "use-places-autocomplete";

export const PLACE_FIELDS = ["geometry", "formatted_address", "address_components"];

const latLngOf = (location) => ({
  lat: typeof location.lat === "function" ? location.lat() : location.lat,
  lng: typeof location.lng === "function" ? location.lng() : location.lng,
});

/**
 * Resolve a picked suggestion to { lat, lng, address, components }.
 * Tries Places details (by place_id), then geocoding by address. Throws if both fail.
 */
export async function resolvePlace({ placeId, description }, deps = { getDetails, getGeocode }) {
  const errors = [];
  if (placeId) {
    try {
      const place = await deps.getDetails({ placeId, fields: PLACE_FIELDS });
      if (place && typeof place === "object" && place.geometry?.location) {
        return {
          ...latLngOf(place.geometry.location),
          address: place.formatted_address || description,
          components: place.address_components || [],
        };
      }
      errors.push("details: no geometry");
    } catch (e) {
      errors.push(`details: ${e?.message || e}`);
    }
  }
  try {
    const results = await deps.getGeocode({ address: description });
    const r = results && results[0];
    if (r?.geometry?.location) {
      return { ...latLngOf(r.geometry.location), address: r.formatted_address || description, components: r.address_components || [] };
    }
    errors.push("geocode: no results");
  } catch (e) {
    errors.push(`geocode: ${e?.message || e}`);
  }
  const err = new Error(`Couldn't look up that address (${errors.join("; ")})`);
  err.causes = errors;
  throw err;
}

/** Address fields stored on Business docs, from Google address components. */
export function parseAddressComponents(components, formattedAddress) {
  const find = (type) => (components || []).find((c) => (c.types || []).includes(type))?.long_name || "";
  return {
    bizAddress: formattedAddress || "",
    bizSuburb: find("locality"),
    bizPostalAddress: find("postal_code"),
    bizAdmin2: find("administrative_area_level_2"), // for precinct
    bizState: find("administrative_area_level_1"),
    bizStreet: find("route"),
    bizStreetNumber: find("street_number"),
    bizCountry: find("country"),
  };
}

/**
 * Address fields for a location: uses the components we already have (from
 * a suggestion pick), else reverse-geocodes (map click). Returns null if the
 * lookup fails.
 */
export async function addressForLocation(location, address, components, deps = { getGeocode }) {
  if (Array.isArray(components) && components.length) {
    return parseAddressComponents(components, address);
  }
  try {
    const r = await deps.getGeocode({ location });
    if (!r || !r[0]) return null;
    return parseAddressComponents(r[0].address_components, r[0].formatted_address);
  } catch {
    return null;
  }
}
